import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { CONTRACT_UX } from "./contractUxTheme";

const resolveVisual = (action) => {
  if (!action) {
    return {
      Icon: CloudUploadIcon,
      accent: CONTRACT_UX.warning,
      soft: CONTRACT_UX.warningSoft,
    };
  }
  if (action.key === "view_projects" || action.severity === "success") {
    return {
      Icon: WorkOutlineIcon,
      accent: CONTRACT_UX.success,
      soft: "rgba(46,125,50,0.12)",
    };
  }
  if (action.key === "sign_contract") {
    return {
      Icon: AssignmentTurnedInOutlinedIcon,
      accent: CONTRACT_UX.warning,
      soft: CONTRACT_UX.warningSoft,
    };
  }
  return {
    Icon: CloudUploadIcon,
    accent: CONTRACT_UX.warning,
    soft: CONTRACT_UX.warningSoft,
  };
};

/**
 * Phase 7.7.4 — Action-first primary card (largest visual weight).
 */
const ContractNextActionCard = ({ action, onActionClick, primaryActionSlot }) => {
  if (!action && !primaryActionSlot) return null;

  const handleClick = (event) => {
    if (typeof onActionClick === "function" && action) {
      onActionClick(action, event);
    }
  };

  const visual = resolveVisual(action);
  const ActionIcon = visual.Icon;
  const borderColor =
    action?.severity === "success"
      ? CONTRACT_UX.success
      : action?.severity === "warning"
      ? CONTRACT_UX.warning
      : CONTRACT_UX.primary;

  return (
    <Box
      data-testid="contract-next-action-card"
      dir="rtl"
      sx={{
        borderRadius: 2,
        border: `1.5px solid ${borderColor}`,
        bgcolor: "#fff",
        px: CONTRACT_UX.cardPadding,
        py: { xs: 2, md: 2.5 },
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 88 },
            minHeight: { xs: 64, md: 88 },
            borderRadius: 2,
            bgcolor: visual.soft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-hidden
        >
          <ActionIcon sx={{ fontSize: 36, color: visual.accent }} />
        </Box>

        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
          {action ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center">
                {(action.key === "upload_guarantee" ||
                  action.key === "view_projects" ||
                  action.key === "wait_completion") && (
                  <CheckCircleIcon
                    sx={{ color: CONTRACT_UX.success, fontSize: 22 }}
                  />
                )}
                <Typography
                  variant="h6"
                  fontWeight={800}
                  data-testid="next-action-title"
                >
                  {action.title}
                </Typography>
              </Stack>
              {action.description ? (
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              ) : null}
            </>
          ) : null}

          {primaryActionSlot}

          {action?.buttonLabel ? (
            action.to ? (
              <Button
                component={RouterLink}
                to={action.to}
                variant="contained"
                size="large"
                startIcon={<ActionIcon />}
                onClick={handleClick}
                data-testid="next-action-button"
                sx={{
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                  bgcolor: CONTRACT_UX.primary,
                  px: 3,
                  py: 1.25,
                  fontWeight: 800,
                  "&:hover": { bgcolor: CONTRACT_UX.primaryDark },
                }}
              >
                {action.buttonLabel}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                startIcon={<ActionIcon />}
                onClick={handleClick}
                data-testid="next-action-button"
                sx={{
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                  bgcolor: CONTRACT_UX.primary,
                  px: 3,
                  py: 1.25,
                  fontWeight: 800,
                  "&:hover": { bgcolor: CONTRACT_UX.primaryDark },
                }}
              >
                {action.buttonLabel}
              </Button>
            )
          ) : null}

          {action?.tip ? (
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: CONTRACT_UX.textMuted, mt: 0.25 }}
              />
              <Typography variant="caption" color="text.secondary">
                {action.tip}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

export default ContractNextActionCard;
